import React, {FC, useCallback, useContext, useState} from 'react';
import {observer} from 'mobx-react';
import {SelectedFilter} from '../../Stores/FiltersStore';
import styles from './FilterSection.module.scss';
import {Option} from '../Select/Select';
import {AppStoreContext} from '../../Stores/AppStore';
import clsx from 'clsx';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faChevronDown, faCircleInfo} from '@fortawesome/free-solid-svg-icons';
import {Popup, SelectBox, Template} from "devextreme-react";
import {LoadIndicator} from 'devextreme-react/load-indicator';
import {popoverDialogStore} from "../PopoverDialog/PopoverDialog";
import {TermsAndConditionsContent} from "../TermsAndConditions/TermsAndConditionsContent";

type FilterSectionProps = {
    onFilterChange: () => void
    hidden?: {
        customer?: boolean
        region?: boolean
        site?: boolean
        plant?: boolean
    }
}

export const FilterSection: FC<FilterSectionProps> = observer(({onFilterChange, hidden}) => {
    const {
        filtersStore: {
            isLoading,
            customerOptions, selectedCustomerOption, onCustomerChange,
            regionOptions, selectedRegionOption, onRegionChange,
            siteOptions, selectedSiteOption, onSiteChange,
            plantOptions, selectedPlantOption, onPlantChange,
            selected
        }
    } = useContext(AppStoreContext);
    const [popupVisible, setPopupVisible] = useState(false);


    const selectedName = selectedPlantOption?.label || selectedSiteOption?.label || selectedRegionOption?.label || selectedCustomerOption?.label || 'Select...';
    const selectedGroup = selectedPlantOption ? 'Plant' : selectedSiteOption ? 'Site' : selectedRegionOption ? 'Region' : '';

    const renderPopup = useCallback(() => {
        const _onChange = (key: keyof SelectedFilter) => (selected: string | boolean | null) => {
            const val = selected as string || '';
            switch (key) {
                case 'customer': {
                    onCustomerChange(val);
                    break;
                }
                case 'region': {
                    onRegionChange(val);
                    break;
                }
                case 'site': {
                    onSiteChange(val);
                    break;
                }
                case 'plant': {
                    onPlantChange(val);
                    break;
                }
            }
            onFilterChange();
        };

        return (
            <div className={styles.filters}>
                {customerOptions.length > 1 &&
                    <div className={styles.item}>
                        <FilterItem
                            isLoading={isLoading.isPending}
                            label="Customer"
                            noClear={true}
                            selected={selected.customer}
                            options={customerOptions}
                            onChange={_onChange('customer')}
                        />
                    </div>
                }
                <div className={clsx(styles.item, hidden?.region && styles.hidden)}>
                    <FilterItem
                        isLoading={isLoading.isPending}
                        label="Region"
                        selected={selected.region}
                        options={regionOptions}
                        onChange={_onChange('region')}
                    />
                </div>
                <div className={clsx(styles.item, hidden?.site && styles.hidden)}>
                    <FilterItem
                        isLoading={isLoading.isPending}
                        label="Site"
                        selected={selected.site}
                        options={siteOptions}
                        onChange={_onChange('site')}
                    />
                </div>
                <div className={clsx(styles.item, hidden?.plant && styles.hidden)}>
                    <FilterItem
                        isLoading={isLoading.isPending}
                        label="Plant"
                        selected={selected.plant}
                        options={plantOptions}
                        onChange={_onChange('plant')}
                    />
                </div>
            </div>
        );
    }, [selected, onFilterChange, onRegionChange, onSiteChange, onPlantChange, isLoading.isPending, customerOptions, hidden, plantOptions, siteOptions, regionOptions, onCustomerChange]);

    const _showFilters = useCallback(() => {
        setPopupVisible(true);
    }, []);

    const _onHiding = useCallback(() => {
        setPopupVisible(false);
    }, []);

    const renderTermsAndConditionsContent = () => {
        return (<TermsAndConditionsContent />);
    }

    return (
        <div className={styles.root}>
            <div className={styles.configs} title={'Terms and Conditions'} onClick={(e) => {
                e.stopPropagation();
                popoverDialogStore.showPopover(renderTermsAndConditionsContent())}}>
                <div className={styles.link} >
                    <FontAwesomeIcon icon={faCircleInfo} className={styles.icon}/>
                </div>
            </div>
            <div className={styles.title} onClick={_showFilters}>
                <div className={styles.selection}>
                    <FontAwesomeIcon icon={faChevronDown} className={styles.icon}/>
                    <div className={styles.name}>
                        <div className={styles.group}>{selectedGroup}</div>
                        {selectedName}
                    </div>
                </div>
            </div>
            <Popup
                showTitle={false}
                dragEnabled={false}
                hideOnOutsideClick={true}
                height="auto"
                maxWidth={360}
                visible={popupVisible}
                onHiding={_onHiding}
                contentRender={renderPopup}
                showCloseButton={false}
            />
        </div>
    );
});

type FilterItemProps = {
    isLoading: boolean
    noClear?: boolean
    label: string
    selected?: string | boolean
    options: Option[]
    onChange: (select: string | boolean | null) => void
}

export const FilterItem: FC<FilterItemProps> = ({label, selected, options, isLoading, onChange, noClear}) => {
    const _changeHandler = (selection: string | boolean | null) => {
        onChange(selection);
    };
    const renderLoadIndicator = useCallback(
        () => isLoading ? <LoadIndicator id="small-indicator" height={20} width={20}/> : null,
        [isLoading],
    );
    return (
        <SelectBox
            items={options}
            label={label}
            value={selected}
            showClearButton={!noClear}
            dropDownButtonTemplate={isLoading ? "loadIndicator" : "dropDownButton"}
            valueExpr="value"
            displayExpr="label"
            onValueChange={_changeHandler}
        >
            <Template
                name="loadIndicator"
                render={renderLoadIndicator}
            />
        </SelectBox>
    );
};